import {useNavigate, useParams} from "react-router-dom";
import AdminLayout from "../../../components/admin/layout/AdminLayout";
import styles from "../../../components/admin/billing/AdminRegularTaxInvoiceInfo.module.css";
import Button from "../../../components/common/button/Button";
import TextCard from "../../../components/common/textCard/TextCard";
import React, {useEffect, useState, useMemo, useCallback} from "react";
import {GetRegularTaxInvoiceResponseData, UpdateRegularTaxInvoiceRequest} from "../../../types/admin";
import {getRegularTaxInvoiceInfoAPI, updateRegularAdminTaxInvoiceAPI} from "../../../services/admin";
import {useAlert} from "../../../components/common/error/AlertContext";

// 어드민 결제 내역 상세 페이지
const EditAdminRegularTaxInvoiceInfoPage: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [data, setData] = useState<GetRegularTaxInvoiceResponseData | null>(null);
    const [form, setForm] = useState<UpdateRegularTaxInvoiceRequest>({
        regularTaxInvoiceId: 0,
        day: 0,
        amount: 0
    });
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const {showAlert} = useAlert();

    // 유효성 검사 함수
    const validateForm = useCallback(() => {
        if (form.day < 1 || form.day > 27) {
            showAlert('발행일은 1일부터 27일 사이여야 합니다.', 'error');
            return false;
        }
        if (form.amount <= 0) {
            showAlert('금액은 0보다 커야 합니다.', 'error');
            return false;
        }
        return true;
    }, [form.day, form.amount, showAlert]);

    // 데이터 세팅
    const fetchData = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const response = await getRegularTaxInvoiceInfoAPI(parseInt(id));
            setData(response);
            setForm({
                regularTaxInvoiceId: response.regularTaxInvoiceId,
                day: response.day,
                amount: response.amount
            });
        } catch (error) {
            console.error('Error fetching payment info:', error);
            showAlert('정기발행 정보를 불러오는데 실패했습니다.', 'error');
        } finally {
            setLoading(false);
        }
    }, [id, showAlert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 폼 제출 핸들러
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setIsSubmitting(true);
            await updateRegularAdminTaxInvoiceAPI(form);
            showAlert('수정되었습니다.', "info");
            navigate(`/admin/billing/regular/${id}`);
        } catch (error) {
            console.error('Error updating Tax Invoice:', error);
            showAlert('수정 중 오류가 발생했습니다.', "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    // 취소 핸들러
    const handleCancel = useCallback(() => {
        navigate(`/admin/billing/regular/${id}`);
    }, [navigate, id]);

    // 발행일 변경 핸들러
    const handleDayChange = useCallback((value: string) => {
        const newDay = parseInt(value.replace(/[^0-9]/g, '')) || 0;
        // 1~27 범위로 제한 (업데이트된 요구사항에 따라)
        const validDay = Math.min(Math.max(newDay, 0), 27);
        setForm(prev => ({
            ...prev,
            day: validDay
        }));
    }, []);

    // 금액 변경 핸들러
    const handleAmountChange = useCallback((value: string) => {
        const newAmount = parseInt(value.replace(/[^0-9]/g, '')) || 0;
        setForm(prev => ({
            ...prev,
            amount: newAmount
        }));
    }, []);

    // 세금 계산 (한국 부가세 10% - 수정된 계산 방식)
    const taxCalculations = useMemo(() => {
        const totalAmount = form.amount;
        // 공급가액 = 총액 ÷ 1.1 (소수점 버림)
        const supplyAmount = Math.floor(totalAmount / 11 * 10);
        // 세액 = 총액 - 공급가액
        const taxAmount = totalAmount - supplyAmount;

        return {
            supplyAmount,
            taxAmount,
            totalAmount
        };
    }, [form.amount]);

    // 발행 정보 데이터 (메모이제이션)
    const regularTaxInvoiceInfo = useMemo(() => {
        if (!data) return [];
        return [
            {
                label: "회사명",
                value: data.companyName
            },
            {
                label: "정기 발행일",
                value: `${form.day}일` || '-',
                type: "text" as const,
                onChange: handleDayChange,
                placeholder: "1-27"
            },
            {
                label: "",
                value: "(1 ~ 27)"
            }
        ];
    }, [data, form.day, handleDayChange]);

    // 금액 정보 데이터 (메모이제이션)
    const amountInfo = useMemo(() => {
        if (!data) return [];
        const {supplyAmount, taxAmount, totalAmount} = taxCalculations;

        return [
            {
                label: "공급가액",
                value: `${supplyAmount.toLocaleString()}원`
            },
            {
                label: "세액",
                value: `${taxAmount.toLocaleString()}원`
            },
            {
                label: "총액",
                value: `${totalAmount.toLocaleString()}원` || '-',
                type: "text" as const,
                onChange: handleAmountChange,
                placeholder: "금액을 입력하세요"
            }
        ];
    }, [data, taxCalculations, handleAmountChange]);

    // 결제 식별자가 없는 경우
    if (!id) {
        return (
            <AdminLayout>
                <div className={styles.error}>잘못된 세금계산서 ID입니다.</div>
            </AdminLayout>
        );
    }

    if (loading) {
        return (
            <AdminLayout>
                <div className={styles.loading}>Loading...</div>
            </AdminLayout>
        );
    }

    if (!data) {
        return (
            <AdminLayout>
                <div className={styles.error}>세금계산서 정보를 찾을 수 없습니다.</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            {/* 결제 상세 정보 */}
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={styles.title}>세금계산서 상세 정보</h2>
                    {/* 임시저장 상태일 경우 발행, 수정, 삭제 버튼 출력 */}
                    <div className={styles.buttons}>
                        {/* 저장 버튼 */}
                        <Button
                            onClick={handleSubmit}
                            variant="primary"
                            className={styles.button}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '저장 중...' : '저장'}
                        </Button>
                        {/* 취소 버튼 */}
                        <Button
                            onClick={handleCancel}
                            variant="secondary"
                            className={styles.button}
                            disabled={isSubmitting}
                        >
                            취소
                        </Button>
                    </div>
                </div>
                <div className={styles.textContainer}>
                    <TextCard items={regularTaxInvoiceInfo}/>
                    <TextCard items={amountInfo}/>
                </div>
            </div>
        </AdminLayout>
    );
};

export default EditAdminRegularTaxInvoiceInfoPage;